(ns admin.components.composite.moment-editor.interactive-timeline
  (:require [re-frame.core :as re-frame]
            [goog.functions :as gfuncs]
            [potpuri.core :as pot]
            [reagent.core :as reagent]
            [reagent-form.utils :as rfu]
            [kyber.components.base.text :as text]
            [kyber.components.base.button :as button]
            [kyber.components.base.icons :as icon]
            [kyber.utils :as ku]
            [kyber.styles.core :refer [gs styles->classes]]
            [admin.events.moments :as >moments]
            [admin.subscriptions.moments :as <moments]
            [admin.styles.constants :refer [moment-timeline-height]]))

(def grab-bar-button-z-index 10)
(def add-moment-container-z-index 10)
(def lam-deadspot-length (* 60 6))

(def classes
  (styles->classes
   {:moment-timeline-container
    {:display :flex
     :align-items :center
     :padding [[(gs [:spacing :p60])
                (gs [:spacing :p8])
                (gs [:spacing :p12])
                (gs [:spacing :p8])]]}

    :timeline-container
    {:width "100%"
     :height (str moment-timeline-height "px")
     :border-radius [[(gs [:radius :r3])
                      (gs [:radius :r3])
                      (gs [:radius :r0])
                      (gs [:radius :r0])]]
     :background-color (gs [:colors :gray-scale-100])
     :position :relative
     :cursor :pointer}

    :timeline-progress-container
    {:position :absolute
     :top (str moment-timeline-height "px")
     :left 0
     :height (gs [:spacing :p2])
     :width "100%"
     :background-color (gs [:colors :gray-scale-200])
     :border-radius [[(gs [:radius :r0])
                      (gs [:radius :r0])
                      (gs [:radius :r3])
                      (gs [:radius :r3])]]}

    :timestamp-container
    {:position :absolute
     :bottom "-40px"
     :background-color (gs [:colors :gray-scale-500])
     :padding [[(gs [:spacing :p2])
                (gs [:spacing :p8])
                (gs [:spacing :p2])
                (gs [:spacing :p8])]]
     :border-radius (gs [:radius :r7])

     "&:before"
     {:content "''"
      :border-style :solid
      :border-left "10px solid transparent"
      :border-right "10px solid transparent"
      :border-bottom (str "10px solid " (gs [:colors :gray-scale-600]))
      :border-top "0"
      :position :absolute
      :top "-8px"
      :left "12px"}}

    :timestamp-copy
    {:color (gs [:colors :true-white])}

    :add-moment-container
    {:position :absolute
     :z-index add-moment-container-z-index
     :top "-45px"
     :background-color (gs [:colors :gray-scale-500])
     :padding [[(gs [:spacing :p2])
                (gs [:spacing :p8])
                (gs [:spacing :p2])
                (gs [:spacing :p8])]]
     :border-radius (gs [:radius :r7])}

    :add-moment-carrot
    {:position :absolute
     :z-index add-moment-container-z-index
     :top "10px"
     :background-color (gs [:colors :gray-scale-600])

     "&:before"
     {:content "''"
      :border-style :solid
      :border-left "10px solid transparent"
      :border-right "10px solid transparent"
      :border-top (str "10px solid " (gs [:colors :gray-scale-600]))
      :border-bottom "0"
      :position :absolute
      :bottom "-8px"}}

    :add-moment-button-container
    {:display :flex
     :align-items :center
     :padding (gs [:spacing :p8])

     "& > *:first-child"
     {:border-right (str "2px solid " (gs [:colors :gray-scale-200]))
      :border-radius [[(gs [:radius :r7])
                       (gs [:radius :r0])
                       (gs [:radius :r0])
                       (gs [:radius :r7])]]}

     "& > *:last-child"
     {:border-radius [[(gs [:radius :r0])
                       (gs [:radius :r7])
                       (gs [:radius :r7])
                       (gs [:radius :r0])]]}}

    :add-moment-button
    {:width "100px"}

    :add-moment-button-icon
    {:color (gs [:colors :true-white])
     :margin-right (gs [:spacing :p8])}

    :moment-marker-container
    {:width "100%"
     :height "100%"
     :position :absolute
     :left 0
     :top 0}

    :current-position-marker
    {:position :absolute
     :top 0
     :left 0
     :height "100%"
     :width (gs [:spacing :p2])
     :background-color (gs [:colors :primary-lite-600])
     :pointer-events :none}

    :marker-range-container
    {:height "100%"
     :position :absolute
     :min-width "2px"
     :transition "background-color 0.4s ease-in-out"}

    :marker-range-background
    {:height "100%"
     :width "100%"
     :background-color (gs [:colors :primary-lite-600])
     :opacity 0.25}

    :grab-bar
    {:position :absolute
     :height "100%"
     :width "1px"
     :top 0
     :background (gs [:colors :gray-scale-600])}

    :grab-bar-time
    {:position :absolute
     :background-color (gs [:colors :gray-scale-500])
     :padding [[(gs [:spacing :p0])
                (gs [:spacing :p8])
                (gs [:spacing :p0])
                (gs [:spacing :p8])]]
     :border-radius (gs [:radius :r7])}

    :grab-bar-button
    {:position :absolute
     :padding [[(gs [:spacing :p4])
                (gs [:spacing :p2])
                (gs [:spacing :p4])
                (gs [:spacing :p2])]]
     :left "-6px"
     :border-radius (gs [:radius :r40])
     :z-index grab-bar-button-z-index
     :cursor :grab
     :box-shadow (gs [:shadows :shadow-12])}

    :grab-bar-button-top
    {:top "-24px"}

    :grab-bar-button-bottom
    {:bottom "-24px"}

    :grab-bar-dark-theme
    {:background (gs [:colors :primary-lite-600])

     "& i"
     {:color (gs [:colors :true-white])}}

    :grab-bar-light-theme
    {:background (gs [:colors :gray-scale-200])}

    :deadspot-range-container
    {:visibility :hidden
     :height "100%"
     :position :absolute
     :border (str "1px solid " (gs [:colors :error-500]))
     :border-bottom :none
     :border-radius [[(gs [:radius :r40])
                      (gs [:radius :r40])
                      (gs [:radius :r0])
                      (gs [:radius :r0])]]
     :top (str "-" moment-timeline-height "px")}}))

(defn- calculate-seek-progress
  [element e]
  (let [element-x (-> element
                      .getBoundingClientRect
                      .-left)
        element-width (.-clientWidth element)
        mouse-x (.-pageX e)]
    (* (/ (- mouse-x element-x) element-width) 100)))

(defn- seek-progress->seek-time
  [{:keys [duration seek-progress]}]
  (* duration (/ seek-progress 100)))

(defn- add-moment
  [{:keys [timeline-state player-key dispatch-local]
    :as moment-details} e]
  (.stopPropagation e)
  (re-frame/dispatch [:media-player/pause-media
                      {:player-key player-key}])
  (re-frame/dispatch [::>moments/add-video-moment
                      (-> moment-details
                          (select-keys [:time
                                        :duration
                                        :type
                                        :behavior
                                        :activity-uuid
                                        :curriculum-uuid])
                          (assoc :player-key player-key))
                      #(dispatch-local [:enter-moment-edit-view :placement])])
  (swap! timeline-state assoc :adding-moment-at nil))

(defn- render-add-moment
  [_]
  (fn [{:keys [duration
               seek-progress
               timeline-state
               dispatch-local
               curriculum-uuid
               activity-uuid
               player-key]}]
    (let [{:keys [adding-moment-at]}
          @timeline-state

          payload
          {:time (seek-progress->seek-time
                  {:duration duration
                   :seek-progress adding-moment-at})
           :player-key player-key
           :duration duration
           :timeline-state timeline-state
           :dispatch-local dispatch-local
           :activity-uuid activity-uuid
           :curriculum-uuid curriculum-uuid}

          add-moment-box-width
          170

          select-moment-type-box-width
          240

          additional-box-padding
          40

          left-percent
          (if (some? adding-moment-at)
            adding-moment-at
            seek-progress)

          left-value
          (if (< left-percent 50)
            additional-box-padding
            (- (if (some? adding-moment-at)
                 select-moment-type-box-width
                 add-moment-box-width)
               additional-box-padding))]
      [:<>
       [:div {:class (:add-moment-carrot classes)
              :style {:left (str "calc(" left-percent "% - 5px)")}}]
       [:div {:class (:add-moment-container classes)
              :style {:left (str "calc(" left-percent "% - " left-value "px)")}}
        (if (some? adding-moment-at)
          [:<>
           [button/icon-button
            {:style {:position :absolute
                     :top "-16px"
                     :right "-12px"
                     :background-color (gs [:colors :true-white])
                     :border-radius (gs [:radius :r40])
                     :box-shadow (gs [:shadows :shadow-12])}
             :on-click #(reset! timeline-state nil)}
            :close]
           [:div {:class (:add-moment-button-container classes)}
            [button/primary-button
             {:class (:add-moment-button classes)
              :data-test "add-note-moment-to-video"

              :on-click (partial add-moment
                                 (assoc payload
                                        :type "note"
                                        :behavior "whisper"))}
             [:span {:style {:display :flex
                             :align-items :center}}
              [icon/icon-sm
               {:class (:add-moment-button-icon classes)}
               :note]
              "Note"]]
            [button/primary-button
             {:class (:add-moment-button classes)
              :data-test "add-question-moment-to-video"
              :on-click (partial add-moment
                                 (assoc payload
                                        :type "question"
                                        :behavior "shout"))}
             [:span {:style {:display :flex
                             :align-items :center}}
              [icon/icon-sm
               {:class (:add-moment-button-icon classes)}
               :list-ordered-light]
              "Question"]]]]
          [:div {:style {:display :flex
                         :align-items :center
                         :padding (gs [:spacing :p8])}}
           [button/primary-button
            {:on-click #(do
                          (re-frame/dispatch [:media-player/pause-media
                                              {:player-key player-key}])
                          (re-frame/dispatch [::>moments/unset-active-moment-uuid])
                          (swap! timeline-state
                                 assoc
                                 :adding-moment-at
                                 seek-progress))
             :style {:border-radius (gs [:radius :r7])}}
            [:span
             [icon/icon-sm
              {:style {:margin-right (gs [:spacing :p8])
                       :color (gs [:colors :true-white])}}
              :plus]
             "Add Moment"]]])]])))

(defn- render-timestamp
  [{:keys [duration seek-progress]}]
  (let [time (-> {:duration duration
                  :seek-progress seek-progress}
                 (seek-progress->seek-time)
                 (ku/seconds->time-string)
                 (ku/fmt-time-string))]
    [:div {:class (:timestamp-container classes)
           :style {:left (str "calc(" seek-progress "% - 20px)")}}
     [text/caption-20 :p
      {:class (:timestamp-copy classes)} time]]))

(defn- render-current-position-marker
  [{:keys [seek-progress]}]
  [:div {:class (:current-position-marker classes)
         :style {:left (str seek-progress "%")}}])

(defn- render-timeline-progress
  [{:keys [duration time]}]
  [:div {:class (:timeline-progress-container classes)}
   [:div {:style {:width (str (ku/calc-percent duration time) "%")
                  :height "100%"
                  :background-color (gs [:colors :primary-lite-800])}}]])

(defn adjust-marker-constraints
  [{:keys [halo-property time behavior start-time end-time display-at hide-at]
    :as marker-props}]
  (cond-> (-> marker-props
              (dissoc :behavior :halo-property)
              (assoc halo-property time))
    ;; Ensures display-at is always before hide-at
    (and (= halo-property :hide-at) (< time display-at))
    (assoc :display-at time)

    ;; Ensures hide-at is always after display-at
    (and (= halo-property :display-at) (> time hide-at))
    (assoc :hide-at time)

    ;; Ensures start time is always the first marker
    (< time start-time)
    (assoc :start-time time)

    (and (= halo-property :start-time) (> time display-at))
    (assoc :display-at time)

    (and (= halo-property :start-time) (> time hide-at))
    (assoc :hide-at time)

    ;; Ensures end time is always the last marker
    (> time end-time)
    (assoc :end-time time)

    (and (= halo-property :end-time) (< time hide-at))
    (assoc :hide-at time)

    (and (= halo-property :end-time) (< time display-at))
    (assoc :display-at time)

    ;; Keeps display-at and hide-at in sync for non whisper moments
    (and (= halo-property :display-at) (= behavior "shout"))
    (assoc :hide-at time)))

(defn- handle-mouse-move-marker
  [{:keys [duration
           property
           timeline-component
           marker-props
           moment-behavior
           on-change]}
   marker-is-active
   _]
  (let [{:keys [start-time end-time display-at hide-at]}
        marker-props

        body-element
        (.-body js/document)

        on-mouse-move
        (gfuncs/throttle
         (fn [e]
           (let [timeline-rect (.getBoundingClientRect @timeline-component)
                 timeline-x (.-left timeline-rect)
                 timeline-width (.-width timeline-rect)
                 mouse-x (.-pageX e)
                 percent-selected (->> timeline-width
                                       (/ (- mouse-x timeline-x))
                                       (* 100)
                                       (max 0)
                                       (min 100))
                 time (->> {:duration duration
                            :seek-progress percent-selected}
                           (seek-progress->seek-time)
                           (.round js/Math))
                 adjusted-marker-props
                 (adjust-marker-constraints
                  {:halo-property property
                   :time time
                   :behavior moment-behavior
                   :start-time start-time
                   :end-time end-time
                   :display-at display-at
                   :hide-at hide-at})]
             (on-change adjusted-marker-props)))
         200)

        on-mouse-up
        (fn on-mouse-up [_]
          (.removeEventListener body-element "mousemove" on-mouse-move)
          (.removeEventListener body-element "mouseup" on-mouse-up)
          (reset! marker-is-active false))]

    (.addEventListener body-element "mousemove" on-mouse-move)
    (.addEventListener body-element "mouseup" on-mouse-up)
    (reset! marker-is-active true)))

(defn- render-marker-handler
  [_]
  (let [marker-is-active (reagent/atom false)]
    (fn [{:keys [duration time position property]
         :as props}]
      [:div {:class (:grab-bar classes)
             :style {:left (str (ku/calc-percent duration time) "%")}}
       [:div {:class (:grab-bar-time classes)
              :style {position "-54px"
                      :left "-20px"}}
        (when (true? @marker-is-active)
          [text/caption-10 :p
           {:style {:color (gs [:colors :true-white])}}
           (-> time
               (ku/seconds->time-string)
               (ku/fmt-time-string))])]

       [button/icon-button
        {:on-mouse-down (partial handle-mouse-move-marker
                                 props
                                 marker-is-active)
         :class (str (:grab-bar-button classes) " "
                     (if (= position :top)
                       (:grab-bar-button-top classes)
                       (:grab-bar-button-bottom classes))
                     " "
                     (if (contains? #{:start-time :end-time} property)
                       (:grab-bar-light-theme classes)
                       (:grab-bar-dark-theme classes)))}
        :grip-lines-vertical]])))

(defn- render-marker-range
  [{:keys [duration
           from
           to
           marker-props
           moment-behavior
           timeline-component
           is-invalid
           editing
           direction
           player-key
           on-change]}]
  (let [from-percent (ku/calc-percent duration (:time from))
        to-percent (if (some? (:time to))
                     (ku/calc-percent duration (:time to))
                     from-percent)
        delta (- to-percent from-percent)]
    [:<>
     [:div {:class (:marker-range-container classes)
            :style (cond-> {:left (str from-percent "%")
                            :width (str delta "%")}
                     (true? is-invalid)
                     (assoc :background-color (gs [:colors :error-500])))}
      [:div {:class (:marker-range-background classes)}]]
     (when (and (or (true? editing)
                    (true? is-invalid))
                (not (true? (:hide-marker from))))
       [render-marker-handler {:position direction
                               :duration duration
                               :time (:time from)
                               :marker-props marker-props
                               :moment-behavior moment-behavior
                               :property (:property from)
                               :timeline-component timeline-component
                               :on-change on-change
                               :player-key player-key}])

     (when (and (or (true? editing)
                    (true? is-invalid))
                (not (true? (:hide-marker to))))
       [render-marker-handler {:position direction
                               :duration duration
                               :time (:time to)
                               :marker-props marker-props
                               :moment-behavior moment-behavior
                               :property (:property to)
                               :timeline-component timeline-component
                               :on-change on-change
                               :player-key player-key}])]))

(defn- render-moment-marker
  [{:keys [moment duration timeline-component player-key
           editor-state dispatch-local]}]
  (let [{active-moment-uuid :moment/uuid}
        @(re-frame/subscribe [::<moments/active-moment])

        {:moment/keys [uuid type]}
        moment

        is-active-moment?
        (= uuid active-moment-uuid)

        {:keys [active-panel editing? temp-moment-form-state]}
        @editor-state

        is-invalid
        (and (-> @editor-state :moment-location-invalid true?)
             is-active-moment?)

        edit-placement-or-halos? (or (= active-panel :placement) (= active-panel :halos))

        start-time ;; for halo
        (if (and is-active-moment? edit-placement-or-halos?)
          (rfu/get-field-value @temp-moment-form-state :start-time)
          (:moment/start-time moment))

        end-time ;; for halo.
        (if (and is-active-moment? edit-placement-or-halos?)
          (rfu/get-field-value @temp-moment-form-state :end-time)
          (:moment/end-time moment))

        display-at ;; for placement
        (if (and is-active-moment? edit-placement-or-halos?)
          (rfu/get-field-value @temp-moment-form-state :display-at)
          (:moment/display-at moment))

        hide-at ;; for placement
        (if (and is-active-moment? edit-placement-or-halos?)
          (rfu/get-field-value @temp-moment-form-state :hide-at)
          (:moment/hide-at moment))

        behavior
        (condp = type
          "question"
          "shout"

          "note"
          (if (rfu/get-field-value @temp-moment-form-state :shout)
            "shout"
            "whisper")
          (js/console.error "Moment type not supported" type))

        marker-props
        {:start-time start-time
         :end-time end-time
         :display-at display-at
         :hide-at hide-at}

        video-seek
        (ku/debounce
         #(re-frame/dispatch [:media-player/seek %])
         250)

        on-marker-change
        (fn [{:keys [time start-time end-time display-at hide-at]}]
          (let [props {:marker-props
                       (pot/assoc-if {}
                                     :start-time start-time
                                     :end-time end-time
                                     :display-at display-at
                                     :hide-at hide-at)}]
            (dispatch-local [:update-temp-moment props])
            (dispatch-local [:set-moment-location-invalid (assoc props :uuid uuid)]))
          (video-seek {:player-key player-key
                       :position time}))]
    (when (some? duration)
      [:li
       (when (and is-active-moment? editing?)
         [render-marker-range
          {:duration duration
           :direction :bottom
           :from {:time start-time
                  :property :start-time}
           :to {:time end-time
                :property :end-time
                :hide-marker false}
           :editing (and is-active-moment? (= :halos active-panel))
           :moment-uuid uuid
           :moment-type type
           :moment-behavior behavior
           :marker-props marker-props
           :timeline-component timeline-component
           :player-key player-key
           :is-invalid (and is-invalid (= :halos active-panel))
           :on-change on-marker-change}])

       [render-marker-range
        {:duration duration
         :direction :top
         :from {:time display-at
                :property :display-at}
         :to {:time hide-at
              :property :hide-at
              :hide-marker (= "shout" behavior)}
         :editing (and is-active-moment? (= :placement active-panel))
         :moment-uuid uuid
         :moment-type type
         :moment-behavior behavior
         :marker-props marker-props
         :timeline-component timeline-component
         :player-key player-key
         :is-invalid (and is-invalid
                          (true? (= :placement active-panel)))
         :on-change on-marker-change}]])))

(defn- render-moment-markers
  [{:keys [moments duration timeline-component
           editor-state player-key dispatch-local]}]
  [:ul {:class (:moment-marker-container classes)}
   (for [{:moment/keys [uuid temp-uuid] :as moment} moments]
     ^{:key (or uuid temp-uuid)}
     [render-moment-marker
      {:editor-state editor-state
       :dispatch-local dispatch-local
       :moment moment
       :duration duration
       :timeline-component timeline-component
       :player-key player-key}])])

(defn- get-deadspots
  [moments duration]
  (let [deadspots
        (reduce
         (fn [acc {:moment/keys [display-at hide-at]}]
           (let [previous-deadspot (last acc)]
             (conj (conj (vec (drop-last acc))
                         (conj previous-deadspot (max (dec display-at) 0)))
                   [(min (inc hide-at) duration)])))
         [[0]]
         (sort-by :moment/display-at moments))]
    (remove
     #(= [0 0] %)
     (update-in deadspots [(dec (count deadspots))] conj duration))))

(defn- render-lam-deadspot
  [{:keys [start end duration]}]
  (let [is-excessive-deadspot (>= (- end start) lam-deadspot-length)
        from-percent (ku/calc-percent duration start)
        to-percent (ku/calc-percent duration end)
        delta (- to-percent from-percent)
        recommended-moments (.round js/Math (/ (- end start)
                                               lam-deadspot-length))]
    ;; TODO - should when clause slurp #4161 ?
    #_(when (and is-excessive-deadspot
                 (not= start end)))
    [:li {:class (:deadspot-range-container classes)
          :style (cond-> {:left (str from-percent "%")
                          :width (str delta "%")}
                   (true? is-excessive-deadspot)
                   (assoc :visibility :visible))}
     [text/caption-10 :p
      {:style {:text-align :center
               :padding (gs [:spacing :p8])
               :padding-bottom (gs [:spacing :p2])
               :padding-top (gs [:spacing :p2])}}
      (str "We recommend adding at least "
           recommended-moments
           " moment"
           (when (> recommended-moments 1) "s"))]]))

(defn- render-lam-deadspots
  [{:keys [duration moments]}]
  (let [deadspots (get-deadspots moments duration)]
    [:ul
     (for [[start end] deadspots]
       ^{:key (str start end)}
       [render-lam-deadspot {:start start
                             :end end
                             :duration duration}])]))

(defn render
  [_]
  (let [!ref
        (atom nil)

        timeline-is-hovered
        (reagent/atom false)

        seek-progress
        (reagent/atom 0)]
    (reagent/create-class
     {:reagent-render
      (fn [{:keys [player-key
                   moments
                   activity-uuid
                   curriculum-uuid
                   player-attributes
                   timeline-state
                   editor-state
                   dispatch-local]}]
        (let [{:keys [time duration]}
              player-attributes

              {editing-moment? :editing?}
              @editor-state

              is-in-timeline
              (and (>= @seek-progress 0)
                   (<= @seek-progress 100))

              {:keys [adding-moment-at]} @timeline-state]
          [:div {:class (:moment-timeline-container classes)}
           (when (some? duration)
             [:div {:class (:timeline-container classes)
                    :ref #(reset! !ref %)

                    :on-mouse-enter
                    #(reset! timeline-is-hovered true)

                    :on-mouse-leave
                    #(reset! timeline-is-hovered false)

                    :on-mouse-move
                    #(reset! seek-progress (calculate-seek-progress @!ref %))

                    :on-click
                    #(when-not (or (some? adding-moment-at)
                                   editing-moment?)
                       (re-frame/dispatch
                        [:media-player/seek
                         {:player-key player-key
                          :position (seek-progress->seek-time
                                     {:duration duration
                                      :seek-progress @seek-progress})}]))}

              (when (and (not editing-moment?)
                         (nil? adding-moment-at))
                [render-lam-deadspots {:duration duration
                                       :moments moments}])

              (when (and (or (true? @timeline-is-hovered)
                             (some? adding-moment-at))
                         (true? is-in-timeline)
                         (not editing-moment?))
                [render-add-moment {:duration duration
                                    :seek-progress @seek-progress
                                    :timeline-state timeline-state
                                    :dispatch-local dispatch-local
                                    :curriculum-uuid curriculum-uuid
                                    :activity-uuid activity-uuid
                                    :player-key player-key}])

              [render-timeline-progress {:duration duration
                                         :time time}]

              [render-moment-markers
               {:moments moments
                :duration duration
                :editor-state editor-state
                :dispatch-local dispatch-local
                :timeline-component !ref
                :player-key player-key}]

              (when (and @timeline-is-hovered
                         is-in-timeline
                         (not editing-moment?)
                         (nil? adding-moment-at))
                [render-current-position-marker
                 {:seek-progress @seek-progress}])

              (when (and (true? @timeline-is-hovered)
                         (true? is-in-timeline)
                         (not editing-moment?)
                         (nil? adding-moment-at))
                [render-timestamp {:duration duration
                                   :seek-progress @seek-progress}])])]))})))
